import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import SessionCard from "./SessionCard";

interface Session {
  id: string;
  created_at: string;
  expires_at: string;
  user_agent: string | null;
  session_token: string | null;
}

interface SessionListProps {
  sessions: Session[];
  currentSessionToken: string | null;
  onSessionRemoved: () => void;
}

const SessionList = ({ sessions, currentSessionToken, onSessionRemoved }: SessionListProps) => {
  const [loggingOutId, setLoggingOutId] = useState<string | null>(null);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  const handleLogoutSession = async (sessionId: string) => {
    setLoggingOutId(sessionId);
    try {
      const { error } = await supabase.rpc('deactivate_session', {
        p_session_token: sessionId
      });

      if (error) throw error;

      toast.success("Sessie succesvol uitgelogd");
      onSessionRemoved();
    } catch (error) {
      console.error('Error deactivating session:', error);
      toast.error("Fout bij uitloggen van sessie");
    } finally {
      setLoggingOutId(null);
    }
  };

  const handleLogoutAllOthers = async () => {
    if (!currentSessionToken) {
      toast.error("Geen actieve sessie gevonden");
      return;
    }

    setLoggingOutAll(true);
    try {
      const { data, error } = await (supabase.rpc as any)('deactivate_all_other_sessions', {
        p_current_session_token: currentSessionToken
      });

      if (error) throw error;

      const count = data || 0;
      if (count > 0) {
        toast.success(`${count} sessie${count === 1 ? '' : 's'} uitgelogd`);
        onSessionRemoved();
      } else {
        toast.info("Geen andere actieve sessies gevonden");
      }
    } catch (error) {
      console.error('Error deactivating sessions:', error);
      toast.error("Fout bij uitloggen van alle sessies");
    } finally {
      setLoggingOutAll(false);
    }
  };

  const activeSessions = sessions.filter(s => new Date(s.expires_at) > new Date());
  const otherSessions = activeSessions.filter(s => s.session_token !== currentSessionToken);

  if (activeSessions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertCircle className="w-12 h-12 mx-auto mb-4" />
        <p>Geen actieve sessies gevonden</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activeSessions.map((session) => (
        <SessionCard
          key={session.id}
          session={session}
          isCurrentSession={session.session_token === currentSessionToken}
          onLogout={handleLogoutSession}
          isLoggingOut={loggingOutId === session.id}
        />
      ))}

      {otherSessions.length > 0 && (
        <div className="pt-4">
          <Button
            variant="destructive"
            onClick={handleLogoutAllOthers}
            disabled={loggingOutAll}
            className="w-full"
          >
            {loggingOutAll ? "Bezig..." : "Log uit op alle andere apparaten"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SessionList;
